import React, { useState, useEffect } from 'react';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const Chat = () => {
  const [chatList, setChatList] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchChatList();
  }, []);

  const fetchChatList = async () => {
    try {
      const response = await apiService.get('chatList');
      if (response.data) {
        setChatList(response.data);
      }
    } catch (error) {
      toast.error('Failed to fetch chat list');
      console.error(error);
    }
  };

  const fetchChatMessages = async (chatId, description) => {
    try {
      const response = await apiService.get(`chatMessageList/${chatId}`);
      if (response.status && response.data) {
        setMessages(response.data);
        setSelectedChat({ id: chatId, description });
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error('Failed to fetch messages');
      console.error(error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 0) return 'In the future';

    let interval = Math.floor(seconds / 31536000);
    if (interval > 1) return `${interval} years ago`;
    if (interval === 1) return '1 year ago';

    interval = Math.floor(seconds / 2592000);
    if (interval > 1) return `${interval} months ago`;
    if (interval === 1) return '1 month ago';

    interval = Math.floor(seconds / 86400);
    if (interval > 1) return `${interval} days ago`;
    if (interval === 1) return '1 day ago';

    interval = Math.floor(seconds / 3600);
    if (interval > 1) return `${interval} hours ago`;
    if (interval === 1) return '1 hour ago';

    interval = Math.floor(seconds / 60);
    if (interval > 1) return `${interval} minutes ago`;
    if (interval === 1) return '1 minute ago';

    return 'Just now';
  };

  const filteredChats = chatList.filter(chat => {
    const senderName = `${chat.senderDetails?.first_name || ''} ${chat.senderDetails?.last_name || ''}`.toLowerCase();
    const receiverName = `${chat.receiverDetails?.first_name || ''} ${chat.receiverDetails?.last_name || ''}`.toLowerCase();
    return senderName.includes(search.toLowerCase()) || receiverName.includes(search.toLowerCase());
  });

  return (
    <div className="flex-1 overflow-hidden bg-gray-50">
      <div className="h-full flex">
        {/* Sidebar - Chat List */}
        <div className="w-96 bg-white border-r flex flex-col">
          <div className="p-4 border-b">
            <div className="relative">
              <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by username or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredChats.map((chat) => {
              const senderName = `${chat.senderDetails?.first_name || 'Sender'} ${chat.senderDetails?.last_name || ''}`;
              const receiverName = `${chat.receiverDetails?.first_name || ''} ${chat.receiverDetails?.last_name || ''}`;
              const firstChar = senderName.charAt(0).toUpperCase();
              const queryDesc = chat.queryDetails?.description
                ? chat.queryDetails.description.substring(0, 50).replace(/\n/g, ' ') + '...'
                : '';
              const timeAgo = chat.last_message_time ? formatTimeAgo(chat.last_message_time) : 'Message not sent yet';

              return (
                <div
                  key={chat._id}
                  onClick={() => fetchChatMessages(chat._id, queryDesc)}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${selectedChat?.id === chat._id ? 'bg-[#009ef7]/10' : ''}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="w-11 h-11 rounded-full bg-red-100 text-red-600 flex items-center justify-center font-semibold">
                        {firstChar}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 text-sm">
                          {senderName} with {receiverName}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{queryDesc}</div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-400">{timeAgo}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col bg-white">
          {selectedChat ? (
            <>
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">
                      {messages[0]?.senderDetails?.first_name} {messages[0]?.senderDetails?.last_name}
                    </div>
                    <div className="flex items-center mt-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      <span className="text-sm text-gray-600">
                        {messages[0]?.receiverDetails?.first_name} {messages[0]?.receiverDetails?.last_name}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">{selectedChat.description}</div>
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.map((msg, index) => {
                  const isVendor = msg.senderDetails?.role === 'vendor';
                  const timeAgo = formatTimeAgo(msg.createdAt);

                  return (
                    <div key={index} className={`flex ${isVendor ? 'justify-start' : 'justify-end'}`}>
                      <div className={`flex flex-col ${isVendor ? 'items-start' : 'items-end'}`}>
                        <div className="flex items-center mb-2 space-x-2">
                          <span className="text-xs text-gray-500">
                            {msg.senderDetails?.first_name} {msg.senderDetails?.last_name}
                          </span>
                          <span className="text-xs text-gray-400">{timeAgo}</span>
                        </div>
                        <div
                          className={`max-w-md p-4 rounded-lg ${
                            isVendor ? 'bg-[#009ef7]/10 text-gray-900' : 'bg-cyan-50 text-gray-900'
                          }`}
                        >
                          {msg.message}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <i className="bi bi-chat-dots text-6xl mb-4"></i>
                <p>Select a chat to view messages</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Chat;
